import { useDraftSaveMode } from 'components/drafts/DraftSaveMode';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import get from 'lodash/get';
import { Control, Controller, FieldValues, SetFieldValue } from 'react-hook-form';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import ReadonlyField from '../rhf-other/ReadonlyField';
import { CommonInputProps, KeyByValue } from './InputTypes';
import InputWrapper from './InputWrapper';

type GetOptionIdentifier<TOption extends FieldValues> =
    | ((option: TOption) => string | number)
    | KeyByValue<TOption, number | string>;
type GetOptionLabel<TOption extends FieldValues> =
    | ((option: TOption) => string)
    | KeyByValue<TOption, number | string | null>;
type GetOptionTooltip<TOption extends FieldValues> = ((option: TOption) => string) | KeyByValue<TOption, JSX.Element>;
type GetOptionDisabled<TOption extends FieldValues> = ((option: TOption) => boolean) | KeyByValue<TOption, boolean>;

interface Props<TFormData extends FieldValues, TOption extends FieldValues> extends CommonInputProps<TFormData> {
    options: TOption[];
    onChange?: (value: TOption | null) => void;
    getOptionLabel: GetOptionLabel<TOption>;
    setValue: SetFieldValue<TFormData>;
    control: Control<TFormData>;
    getOptionIdentifier: GetOptionIdentifier<TOption>;
    getOptionTooltip?: GetOptionTooltip<TOption>;
    getOptionDisabled?: GetOptionDisabled<TOption>;
    sortDirection?: 'asc' | 'desc';
    readonly?: boolean;
    gap?: number;
    /** One line of options. Unset, a record section still draws one line; `false` keeps the column there too. */
    row?: boolean;
    ariaLabelledBy?: string;
}

const RadioInput = <TFormData extends FieldValues, TOption extends FieldValues>({
    name,
    label,
    required,
    rightLabelComponent,
    tooltip,
    disabled,
    wrapperClassName,
    control,
    options,
    onChange,
    getOptionLabel,
    getOptionDisabled,
    getOptionIdentifier,
    getOptionTooltip,
    sortDirection,
    readonly = false,
    gap,
    row,
    ariaLabelledBy,
}: Props<TFormData, TOption>) => {
    const getLabel = (option: TOption | null) => {
        if (!option) {
            return null;
        }

        if (typeof getOptionLabel === 'string') {
            return get(option, getOptionLabel);
        } else {
            return getOptionLabel(option);
        }
    };

    const getValue = (option: TOption | null) => {
        if (!option) {
            return null;
        }

        if (typeof getOptionIdentifier === 'string') {
            return get(option, getOptionIdentifier);
        } else {
            return getOptionIdentifier(option);
        }
    };

    const getDisabled = (option: TOption | null): boolean => {
        if (!option || getOptionDisabled === undefined) {
            return false;
        }
        if (typeof getOptionDisabled === 'string') {
            return get(option, getOptionDisabled);
        } else {
            return getOptionDisabled(option);
        }
    };

    const getTooltip = (option: TOption | null) => {
        if (!option) {
            return null;
        }

        if (typeof getOptionTooltip === 'string') {
            return get(option, getOptionTooltip);
        }

        if (typeof getOptionTooltip === 'function') {
            return getOptionTooltip(option);
        }

        return null;
    };

    const getSortedOptions = (options: TOption[], direction: 'asc' | 'desc' | undefined) => {
        if (direction === undefined) {
            return options;
        }

        return options.toSorted((a, b) => {
            if (direction === 'asc') {
                return String(getLabel(a)).localeCompare(String(getLabel(b)));
            } else {
                return String(getLabel(b)).localeCompare(String(getLabel(a)));
            }
        });
    };

    const sortedOptions = getSortedOptions(options, sortDirection);
    // In a record section the options are one line on a row of their own, in the 36px box of a read value,
    // unless the caller says otherwise.
    const inSection = useInRecordSection();
    const isRow = row ?? inSection;
    // react-hook-form focuses this on a failed submit. A disabled radio would accept the ref and
    // then swallow the focus — Controller's ref proxy always reports a `focus` method, so RHF
    // counts it as handled and stops before reaching any later invalid field.
    const focusableIndex = disabled ? -1 : sortedOptions.findIndex((option) => !getDisabled(option));
    const drafting = useDraftSaveMode();

    return (
        <Controller
            control={control}
            name={name}
            // Always required, whatever `required` says: that prop only marks the field so (its asterisk, its tint
            // and count while nothing is chosen, and its place in the create dialog's Required section). A draft
            // is saved without it.
            rules={{ required: { value: !drafting, message: t('validation.required-field') } }}
            render={({ field: { ref, value, onBlur, onChange: innerOnChange }, fieldState: { error } }) => (
                <InputWrapper
                    name={name}
                    label={label}
                    required={required}
                    isEmpty={value === undefined || value === null || value === ''}
                    tooltip={tooltip}
                    rightLabelComponent={rightLabelComponent}
                    readonly={!!readonly}
                    hasErrors={!!error}
                    error={readonly ? undefined : error}
                    className={cn(inSection && isRow && 'col-span-full', wrapperClassName)}
                    gap={gap}
                >
                    {readonly ? (
                        <ReadonlyField
                            // The stored value is always the stringified identifier (see the RadioGroup
                            // below), so a numeric `getOptionIdentifier` only matches through String().
                            value={options.find((val) => String(getValue(val)) === String(value)) ?? value}
                            getLabel={getLabel}
                            getValue={getValue}
                        />
                    ) : (
                        <RadioGroup
                            name={name}
                            aria-labelledby={ariaLabelledBy}
                            onBlur={onBlur}
                            // Each option is a 34px row with no gap between rows, and inline options sit
                            // 14px apart.
                            className={cn(
                                'gap-0',
                                isRow && 'flex flex-row flex-wrap gap-x-3.5',
                                isRow && inSection && 'min-h-9 items-center'
                            )}
                            // The identifier is stringified on the way in and out, so a numeric one
                            // still stores the string the DOM-event-based version always stored.
                            value={value == null ? null : String(value)}
                            onValueChange={(next) => {
                                innerOnChange(next);
                                onChange?.(options.find((option) => String(getValue(option)) === next) ?? null);
                            }}
                        >
                            {sortedOptions.map((option, index) => (
                                <label
                                    key={`${String(getValue(option))}-label`}
                                    className={cn(
                                        'flex h-8.5 w-fit flex-row items-center gap-2 text-sm',
                                        disabled || getDisabled(option)
                                            ? 'text-fg-disabled cursor-not-allowed'
                                            : 'text-fg cursor-pointer'
                                    )}
                                >
                                    <RadioGroupItem
                                        value={String(getValue(option))}
                                        disabled={disabled || getDisabled(option)}
                                        ref={index === focusableIndex ? ref : undefined}
                                    />
                                    <span className="flex flex-row items-center gap-1">
                                        {getLabel(option)}
                                        {getTooltip(option)}
                                    </span>
                                </label>
                            ))}
                        </RadioGroup>
                    )}
                </InputWrapper>
            )}
        />
    );
};

export default RadioInput;
